import { Backdrop, CircularProgress, IconButton } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@material-ui/icons';
import MUIDataTable from "mui-datatables";
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import TextField from '../../components/Form/TextField';
import AlertDialog from '../../components/ui/AlertDialog';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import { useAuth } from '../../hooks/auth';
import { Concierge, ConciergeHost, ConciergePost, ConciergePut } from '../../interfaces/Concierge';
import ConciergeApi from '../../services/MvmApi';
import getDefaultGridOptions from '../../utils/componentUtils';
import permissions from '../../utils/Permissions';
import { useStyles } from './Style';

type Props = {
    conciergeId: number,
    clickHandlerCallback?: () => void,
    clickHandlerClose?: () => void,
    handleSetSuccess?: (value: boolean) => void,
}

interface IFormInput {
    code: any;
    newHost: any;
}


const ConciergeForm: React.FC<Props> = (props) => {
    const classes = useStyles();
    const [concierge, setConcierge] = useState<Concierge | any>();
    const [hosts, setHosts] = useState<ConciergeHost[]>([]);
    const [loading, setLoading] = useState(true);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const { t } = useTranslation("Concierge");
    const { t: _t } = useTranslation("General");
    const { t: tval } = useTranslation("Validation");
    const { enqueueSnackbar } = useSnackbar();
    const { user, hasPermission } = useAuth();
    const refCampoNewHost = useRef<HTMLElement>(null)
    const [hostsErrorMessage, setHostsErrorMessage] = useState<string>('');
    const history = useHistory();

    //Metodos do form:
    const { handleSubmit, reset, control, getValues, setError, resetField, setValue, setFocus } = useForm<IFormInput>({
        defaultValues: {
            code: "",
            newHost: ""
        }
    });


    const getConcierge = useCallback(
        (concierge: number) => {
            if (props.conciergeId && props.conciergeId !== 0) {
                setLoading(true);
                ConciergeApi.getConcierge(props.conciergeId).then((result) => {
                    if (result) {
                        setHosts(result.conciergeHosts)
                        reset({
                            code: result.code
                        });
                    }
                }).finally(() => {
                    setLoading(false);
                });
            }
            else {
                setHosts([]);
                reset({
                    code: "",
                    newHost: ""
                });
            }
        },
        [props]
    );

    useEffect(() => {
        setLoading(false);
        getConcierge(props.conciergeId);
        
    }, [props, getConcierge]);

    useEffect(() =>{
        if (!hasPermission(permissions.CONCIERGE.Add)){
            history.replace('NoPermission');
        }
    }, [history, user])
    
    const yupValidationSaveForm = async (data: any) => {
        var resp = false;
        try {
            const schema = Yup.object().shape({
                code: Yup.string()
                    .required(tval("requiredField"))
                    .max(4, tval("requiredLength4")),
                })
                
            await schema.validate(data, {
                abortEarly: false,
            })

            resp = true
        }  catch (err) {

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "code" | "newHost" , { message: error.message });
                })
            }

            resp = false
        }

        if (hosts.length === 0) {
            setHostsErrorMessage(t('validationHosts'))
            resp = false
        } else {
            setHostsErrorMessage('')
        }        
        
        return resp
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: hosts.length > 0 ? 'pointer' : ''
                }
            }
        }
    })

    const columns = [
        {
            name: 'userName',
            label: String(t('descriptionHosts')),
            options: {
                setCellProps: () => ({
                    align: "left"
                })
            }
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <AlertDialog
                            dialogTitle={t("dialogDeleteTitle")}
                            dialogContentText={t("dialogDeleteContentText")}
                            cancelTextButton={t("cancelDeleteTextButton")}
                            confirmTextButton={t("confirmDeleteTextButton")}
                            confirmDialogFunction={() => { handleDeleteItem(tableMeta.rowData[0]); }}
                            Button={
                                <IconButton size='small' edge="end">
                                    <DeleteIcon />
                                </IconButton>
                            }
                        />
                    );
                }
            }
        }
    ]

    function handleDeleteItem(userName: string) {
        setHosts(hosts.filter(item => item.userName !== userName));
    };

    //Incluir host na lista:
    function handleIncludeItem() {
        setHostsErrorMessage('')
        
        let formData = getValues();
        if (formData.newHost) {

            if (hosts.find(item => item.userName == formData.newHost)) {
                setHostsErrorMessage(t('existingHostErrorMessage'))
                return;
            }

            let newItem:ConciergeHost = {
                conciergeId: props.conciergeId, 
                userName: formData.newHost,
            };
              
            setHosts([newItem, ...hosts]);

            setValue('newHost', ''); //TODO: resetField('newHost') nao reseta quando estiver no form de edicao - PQ?
            //resetField('newHost');
            refCampoNewHost.current?.focus();
        }
    };

    const onSubmit = async (data: any) => {

        if(!(await yupValidationSaveForm(data)))
            return;

        setOpenBackdrop(true);

        if (props.conciergeId && props.conciergeId !== 0) {
            let put:ConciergePut = {
                id: props.conciergeId,
                code: data.code,
                plantId: Number(user.company.plantID),
                deleted: concierge ? concierge.deleted : false,
                includedHosts: []
            };

            hosts.forEach(element => {
                put.includedHosts.push(element.userName);
            });

            handleSubmitPut(put);
        }
        else{
            let post:ConciergePost = {
                code: data.code,
                plantId: Number(user.company.plantID),
                deleted: concierge ? concierge.deleted : false,
                includedHosts: []
            };

            hosts.forEach(element => {
                post.includedHosts.push(element.userName);
            });

            handleSubmitPost(post);
        }
    };

    const handleSubmitPost = useCallback(
        async (data: ConciergePost) => {
            ConciergeApi.postConcierge(data)
                .then(async (result) => {
                    enqueueSnackbar(t("returnSubmitFormSuccess"), { variant: 'success' });
                    if (props && props.handleSetSuccess){
                        props.handleSetSuccess(true);
                    }
                    if (props && props.clickHandlerCallback)
                        props.clickHandlerCallback()
                }).finally(() => {
                    setOpenBackdrop(false);
                });
        },
        [props, enqueueSnackbar, t]
    );

    const handleSubmitPut = useCallback(
        async (data: ConciergePut) => {
                ConciergeApi.putConcierge(data)
                    .then(async (result) => {
                        enqueueSnackbar(t("returnSubmitEditFormSuccess"), { variant: 'success' });
                        if (props && props.handleSetSuccess){
                            props.handleSetSuccess(true);
                        }if (props && props.clickHandlerCallback)
                            props.clickHandlerCallback()
                    }).finally(() => {
                        setOpenBackdrop(false);
                    });
        },
        [props, enqueueSnackbar, t]
    );

    return (

        loading ?
            <LoadingUI />
            :
            <React.Fragment>

                <Backdrop className={classes.backdrop} open={openBackdrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <form translate={undefined} onSubmit={handleSubmit(onSubmit) } id="formConcierge">     

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label={t("code")}
                                name="code"
                                control={control}
                                className={classes.rootForm}
                                disabled={props.conciergeId && props.conciergeId !== 0 ? true : false}
                                style={{
                                    margin: '0px',
                                    
                                }}
                                fullWidth
                                inputProps={{
                                    maxLength: 4,
                                  }}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                label={t('includeHostDialog')}
                                name="newHost"
                                inputRef={refCampoNewHost}
                                control={control}
                                inputProps={{ maxLength: 44 }}
                                fullWidth
                                style={{ margin: '0px' }}
                                autoComplete="off"
                                onKeyPress={(e) => { if (e.key == "Enter") { handleIncludeItem(); e.preventDefault(); } }}

                            />
                        </Grid>
                        
                        <Grid item xs={2}>
                            <ButtonUI
                                color="primary"
                                variant="contained"
                                onClick={handleIncludeItem}
                                //disabled={!ip.isV4Format(newHost)}
                                style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px'   }}
                            >
                                <AddIcon />
                            </ButtonUI>
                        </Grid>
                        
                        {/* Msg de validacao da lista de hosts: */}
                        {
                            hostsErrorMessage ? 
                            <Grid item xs={12} className='MuiFormHelperText-root Mui-error'>
                                <p style={{ color: 'red' }}>{hostsErrorMessage}</p>
                            </Grid>
                            : <></>
                        }
                        

                        <Grid item xs={12}>
                            <MuiThemeProvider theme={myTheme}>
                                <MUIDataTable
                                    title=""
                                    data={hosts}
                                    columns={columns}
                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: false,
                                        pagination: false,
                                        sort: false,
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        download: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        tableBodyMaxHeight: `${window.innerHeight - 325}px`
                                    }}
                                />
                            </MuiThemeProvider>
                        </Grid>

                        <Grid item  xs={12} className={classes.footForm}>
                            <Button variant="contained" className={classes.rootCancelButton} onClick={() => props && props.clickHandlerClose ? props.clickHandlerClose() : null}>
                                {t("cancel")}
                            </Button>

                            <Button type="submit" variant="contained" color="primary" className={classes.rootButton}>
                                {t("confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>

            </React.Fragment>

    )
}
export default ConciergeForm;
